import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import {
  updatesApi as up,
  voicemailMoveApi as vm,
  type UpdateAction,
  type UpdateDetail,
  type UpdateId,
  type UpdateInfo,
  type UpdateRow,
  type UpdateStateName,
  type UpdateSummary
} from '../../api/client';
import { Button, Card, Chip, Dialog, Drawer, Empty, LoadError, Loading, Notice, PageHeader, Toast } from '../../components/ui';
import { FieldTestReminder } from '../../components/FieldTestReminder';
import { plural, shortWhen, type Tone } from '../../lib/format';
import { useData } from '../../lib/useData';
import { VoicemailMailboxes } from './VoicemailEmails';

/**
 * Stitch Admin → Updates (1.12.33, Teampace 2992). Joey, Oct 30 2026: "when we pull up a customer
 * in the platform we can see what needs to be migrated or updated and can roll out the features one
 * customer at a time for now. We will also want a way to do that in mass as well."
 *
 * The list of updates with how many customers stand where, and each update's page: every customer,
 * ticked one by one or all that need it, then Check, Apply or Put back. Each customer's page has the
 * same updates just for it (UpdatesCard).
 */

export const UPDATE_STATE: Record<UpdateStateName, { label: string; tone: Tone }> = {
  done: { label: 'Done', tone: 'ok' },
  needed: { label: 'Needs it', tone: 'warn' },
  partial: { label: 'Partly done', tone: 'warn' },
  problem: { label: 'Problem', tone: 'crit' },
  unavailable: { label: 'Not possible', tone: 'neutral' },
  unknown: { label: 'Not checked', tone: 'neutral' }
};

const RUNNING: Record<UpdateAction, string> = { check: 'Checking…', apply: 'Applying…', put_back: 'Putting back…' };

/** Reloads every two seconds while anything is going. */
export function usePoll(going: boolean, reload: () => void) {
  useEffect(() => {
    if (!going) return;
    const t = setInterval(reload, 2000);
    return () => clearInterval(t);
  }, [going, reload]);
}

export function StateChip({ row }: { row: Pick<UpdateRow, 'state' | 'running' | 'leftOut'> }) {
  if (row.running) return <Chip tone="brand">{RUNNING[row.running]}</Chip>;
  if (row.leftOut) return <Chip>Left out</Chip>;
  const s = UPDATE_STATE[row.state];
  return <Chip tone={s.tone}>{s.label}</Chip>;
}

function counted(u: UpdateSummary): [string, number, Tone][] {
  const c = u.counts;
  const list: [string, number, Tone][] = [
    ['Needs it', c.needed + c.partial, 'warn'],
    ['Problem', c.problem, 'crit'],
    ['Not checked', c.unknown, 'neutral'],
    ['Done', c.done, 'ok'],
    ['Not possible', c.unavailable, 'neutral'],
    ['Left out', c.leftOut, 'neutral']
  ];
  return list.filter(([, n]) => n > 0);
}

/** Stitch Admin → Updates: every update and where customers stand. */
export function AdminUpdates() {
  const data = useData(() => up.list(), []);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const going = Boolean(data.data?.updates.some((u) => u.counts.running > 0));
  usePoll(going, data.reload);

  const [asking, setAsking] = useState(false);
  const [bringing, setBringing] = useState(false);
  const bringAll = async () => {
    setError('');
    setBringing(true);
    try {
      const r = await up.bringUpToDate();
      setAsking(false);
      setToast(
        r.customers
          ? `Bringing ${plural(r.customers, 'customer', 'customers')} up to date, one after another.${r.skipped.length ? ` ${plural(r.skipped.length, 'customer', 'customers')} skipped: something is already going for ${r.skipped.length === 1 ? 'it' : 'them'}.` : ''}`
          : 'Nothing to do: no customers with a tenant (that aren’t suspended).'
      );
      data.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBringing(false);
    }
  };

  const checkAll = async (u: UpdateSummary) => {
    setError('');
    try {
      const r = await up.run(u.id, 'check', 'all');
      setToast(`Checking ${plural(r.started, 'customer', 'customers')} for ${u.title}.`);
      data.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Stitch Admin"
        title="Updates"
        actions={
          <>
            <Button icon="refresh" onClick={data.reload} disabled={data.loading}>
              Refresh
            </Button>
            <Button variant="primary" onClick={() => setAsking(true)} disabled={!data.data || going}>
              Bring every customer up to date
            </Button>
          </>
        }
      />
      <div className="content">
        <Notice title="Bring customers up to date, one at a time or many at once">
          Each update checks where a customer stands, brings it up to date, and (where it can) puts it back. Open one to tick customers, or every one that needs it.
          Each customer’s page in Customers shows the same updates just for them. A customer gets every update by itself as soon as its tenant is linked.
        </Notice>
        {error && <Notice tone="crit">{error}</Notice>}
        {data.error ? (
          <LoadError error={data.error} retry={data.reload} />
        ) : !data.data ? (
          <Loading label="Loading updates" />
        ) : (
          <div className="updates-grid">
            {data.data.updates.map((u) => (
              <Card
                key={u.id}
                title={u.title}
                action={u.counts.running ? <Chip tone="brand">{`${u.counts.running} going`}</Chip> : undefined}
                foot={
                  <span className="row-actions">
                    <Button size="small" onClick={() => void checkAll(u)} disabled={u.counts.running > 0}>
                      Check all
                    </Button>
                    <Link className="btn small primary" to={`/admin/updates/${u.id}`}>
                      Open
                    </Link>
                  </span>
                }
              >
                <div className="card-body section-stack">
                  <p className="muted">{u.about}</p>
                  <div className="chip-row" aria-label={`${u.title}: where customers stand`}>
                    {counted(u).length ? (
                      counted(u).map(([label, n, tone]) => (
                        <Chip key={label} small tone={tone}>
                          {`${label} · ${n}`}
                        </Chip>
                      ))
                    ) : (
                      <span className="muted">No customers with a tenant yet.</span>
                    )}
                  </div>
                  <small className="muted">{u.lastCheckedAt ? `Last checked ${shortWhen(u.lastCheckedAt)}` : 'Not checked yet'}</small>
                  {/* The field tests that check it, with their newest results (3124): a warning only. */}
                  <FieldTestReminder tests={data.data?.fieldTests?.[u.id]} what={u.title} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      {toast && <Toast tone="ok" message={toast} onDismiss={() => setToast('')} />}
      <Dialog
        open={asking}
        onClose={() => setAsking(false)}
        title="Bring every customer up to date?"
        foot={
          <>
            <Button onClick={() => setAsking(false)}>Cancel</Button>
            <Button variant="primary" onClick={() => void bringAll()} disabled={bringing}>
              {bringing ? 'Starting…' : 'Bring them up to date'}
            </Button>
          </>
        }
      >
        <div className="section-stack">
          <FieldTestReminder tests={data.data?.fieldTests?.['bring-up-to-date']} what="Bring every customer up to date" />
          <p>
            Stitch checks every customer with a tenant, one after another, and applies each update it needs, in this order: App calling, voicemail emails, old voicemail (make
            room), then Patch rings.
          </p>
          <ul className="plain-list">
            <li>Customers that are suspended, or left out of an update, are skipped for it. So is an update staff put back on purpose: apply that one from its own page.</li>
            <li>
              App calling: where the phone system doesn’t show SIP passwords, Stitch sets its own on the extensions of people signed in to Stitch. A softphone or phone set up
              by hand with the old password then needs the new one; provisioned phones aren’t affected.
            </li>
            <li>
              It changes customers’ phone systems: voicemail emails move to Stitch, full mailboxes start deleting their oldest messages to make room, and numbers that go
              nowhere are put in open inventory (answered with the company’s name). Each change is read back, and can be put back from the update’s page.
            </li>
            <li>It runs in the background. Each customer’s line on an update’s page shows what was done.</li>
          </ul>
        </div>
      </Dialog>
    </>
  );
}

type Filter = 'all' | 'needs' | 'done' | 'problem' | 'unknown' | 'left';
const FILTERS: [Filter, string][] = [
  ['needs', 'Needs it'],
  ['problem', 'Problems'],
  ['unknown', 'Not checked'],
  ['done', 'Done'],
  ['left', 'Left out'],
  ['all', 'All']
];
const inFilter = (r: UpdateRow, f: Filter) =>
  f === 'all'
    ? true
    : f === 'left'
      ? r.leftOut
      : r.leftOut
        ? false
        : f === 'needs'
          ? r.state === 'needed' || r.state === 'partial'
          : f === 'done'
            ? r.state === 'done'
            : f === 'problem'
              ? r.state === 'problem'
              : r.state === 'unknown' || r.state === 'unavailable';

interface Ask {
  action: UpdateAction;
  ids: string[];
}

/** One update: every customer, ticked one by one or all that need it. */
export function AdminUpdate() {
  const { id = '' } = useParams();
  const data = useData(() => up.one(id), [id]);
  const policy = useData(() => (id === 'mailbox-policy' ? up.list().then((l) => l.mailboxPolicy) : Promise.resolve(null)), [id]);
  const [filter, setFilter] = useState<Filter>('needs');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [ask, setAsk] = useState<Ask | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [open, setOpen] = useState<UpdateRow | null>(null);
  const [leaving, setLeaving] = useState<UpdateRow | null>(null);
  const v = data.data;
  const going = Boolean(v?.rows.some((r) => r.running));
  usePoll(going, data.reload);
  // The first look: show what needs it when anything does, else everything.
  const rows = useMemo(() => v?.rows.filter((r) => r.domain) ?? [], [v]);
  const shown = rows.filter((r) => inFilter(r, filter));
  const unlinked = (v?.rows.length ?? 0) - rows.length;
  const pickable = shown.filter((r) => !r.running);
  const allPicked = pickable.length > 0 && pickable.every((r) => picked.has(r.customerId));
  const u = v?.update;

  const toggle = (cid: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(cid)) n.delete(cid);
      else n.add(cid);
      return n;
    });

  const go = async (a: Ask) => {
    setBusy(true);
    setError('');
    try {
      const r = await up.run(id, a.action, a.ids);
      const verb = a.action === 'check' ? 'Checking' : a.action === 'apply' ? (u?.applyLabel ?? 'Applying') : (u?.putBackLabel ?? 'Putting back');
      setToast(
        [
          r.started ? `${verb}: ${plural(r.started, 'customer', 'customers')}.` : 'Nothing started.',
          r.skipped.length ? `${plural(r.skipped.length, 'customer', 'customers')} skipped: ${r.skipped.map((s) => `${s.name} (${s.why.replace(/\.$/, '')})`).join('; ')}.` : ''
        ]
          .filter(Boolean)
          .join(' ')
      );
      setPicked(new Set());
      setAsk(null);
      data.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const needing = rows.filter((r) => !r.leftOut && !r.running && (r.state === 'needed' || r.state === 'partial') && !r.blocked).map((r) => r.customerId);
  const ids = [...picked];

  return (
    <>
      <PageHeader
        eyebrow={<Link to="/admin/updates">Updates</Link>}
        title={u?.title ?? 'Update'}
        actions={
          <Button icon="refresh" onClick={data.reload} disabled={data.loading}>
            Refresh
          </Button>
        }
      />
      <div className="content">
        {data.error ? (
          <LoadError error={data.error} retry={data.reload} />
        ) : !v || !u ? (
          <Loading label="Loading customers" />
        ) : (
          <>
            <p className="muted page-intro">{u.about}</p>
            {u.needs.length > 0 && <Notice>{`Needs ${u.needs.map((n) => titleOf(n)).join(' and ')} first: customers without it are skipped.`}</Notice>}
            <FieldTestReminder tests={v.fieldTests} what={u.title} />
            {id === 'mailbox-policy' && policy.data && <StandardPolicy standard={policy.data.standard} choices={policy.data.choices} onSaved={(m) => (setToast(m), policy.reload())} />}
            {error && <Notice tone="crit">{error}</Notice>}
            <Card
              title="Customers"
              action={<span className="muted">{`${plural(rows.length, 'customer', 'customers')} with a tenant${unlinked ? ` · ${unlinked} not linked yet` : ''}`}</span>}
            >
              <div className="card-body section-stack">
                <div className="updates-toolbar">
                  <div className="segmented" role="group" aria-label="Show">
                    {FILTERS.map(([f, label]) => {
                      const n = rows.filter((r) => inFilter(r, f)).length;
                      return (
                        <button key={f} type="button" aria-pressed={filter === f} className={filter === f ? 'on' : undefined} onClick={() => setFilter(f)}>
                          {`${label} ${n}`}
                        </button>
                      );
                    })}
                  </div>
                  <span className="grow" />
                  <Button size="small" onClick={() => setAsk({ action: 'check', ids: ids.length ? ids : rows.filter((r) => !r.running).map((r) => r.customerId) })} disabled={busy || rows.length === 0}>
                    {ids.length ? `Check ${ids.length}` : 'Check all'}
                  </Button>
                  {u.putBackLabel && (
                    <Button size="small" onClick={() => setAsk({ action: 'put_back', ids })} disabled={busy || ids.length === 0}>
                      {`${u.putBackLabel}${ids.length ? ` (${ids.length})` : ''}`}
                    </Button>
                  )}
                  <Button size="small" variant="primary" onClick={() => setAsk({ action: 'apply', ids })} disabled={busy || ids.length === 0}>
                    {`${u.applyLabel}${ids.length ? ` (${ids.length})` : ''}`}
                  </Button>
                </div>
                {needing.length > 0 && (
                  <div className="row-actions">
                    <Button size="small" variant="ghost" onClick={() => setPicked(new Set(needing))}>
                      {`Tick all ${needing.length} that need it`}
                    </Button>
                  </div>
                )}
                {shown.length === 0 ? (
                  <Empty title={filter === 'needs' ? 'Nobody needs it' : 'Nobody here'}>
                    {filter === 'needs' ? 'Every customer that’s been checked has it, or was left out. Check again to be sure.' : 'Choose another filter above.'}
                  </Empty>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col" className="check-col">
                            <input
                              type="checkbox"
                              aria-label="Tick every customer shown"
                              checked={allPicked}
                              onChange={() => setPicked(allPicked ? new Set() : new Set(pickable.map((r) => r.customerId)))}
                            />
                          </th>
                          <th scope="col">Customer</th>
                          <th scope="col">Where it stands</th>
                          <th scope="col" className="hide-sm">
                            Last done
                          </th>
                          <th scope="col">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {shown.map((r) => (
                          <tr key={r.customerId}>
                            <td className="check-col">
                              <input type="checkbox" aria-label={`Tick ${r.name}`} checked={picked.has(r.customerId)} disabled={Boolean(r.running)} onChange={() => toggle(r.customerId)} />
                            </td>
                            <td>
                              <span className="cell-stack">
                                <Link to={`/admin/customers/${r.customerId}`}>{r.name}</Link>
                                <small className="mono">{r.domain}</small>
                              </span>
                            </td>
                            <td>
                              <span className="cell-stack">
                                <span>
                                  <StateChip row={r} />
                                </span>
                                <small>{r.leftOut ? r.leftOutNote || 'Left out on purpose.' : r.blocked && r.state !== 'done' ? `${r.summary} ${r.blocked}` : r.summary}</small>
                                {r.checkedAt && <small className="muted">{`Checked ${shortWhen(r.checkedAt)}`}</small>}
                              </span>
                            </td>
                            <td className="hide-sm">
                              {r.lastAction ? (
                                <span className="cell-stack">
                                  <span>{`${r.lastAction === 'apply' ? u.applyLabel : (u.putBackLabel ?? 'Put back')} · ${r.changedBy ?? ''}`}</span>
                                  <small>{r.lastResult}</small>
                                  {r.changedAt && <small className="muted">{shortWhen(r.changedAt)}</small>}
                                </span>
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </td>
                            <td>
                              <span className="row-actions">
                                {hasDetail(u.id) && (
                                  <Button size="small" variant="ghost" onClick={() => setOpen(r)} aria-label={`Details for ${r.name}`}>
                                    Details
                                  </Button>
                                )}
                                <Button size="small" variant="ghost" onClick={() => (r.leftOut ? void leaveOut(id, r, false, '', data.reload, setError) : setLeaving(r))} disabled={Boolean(r.running)}>
                                  {r.leftOut ? 'Put back in' : 'Leave out'}
                                </Button>
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Card>
          </>
        )}
      </div>

      <Dialog
        open={Boolean(ask && u)}
        onClose={() => setAsk(null)}
        title={ask && u ? confirmTitle(u, ask) : ''}
        foot={
          <>
            <Button variant="ghost" onClick={() => setAsk(null)}>
              Cancel
            </Button>
            <Button variant={ask?.action === 'put_back' ? 'danger' : 'primary'} disabled={busy} onClick={() => ask && void go(ask)}>
              {busy ? 'Starting…' : ask && u ? confirmButton(u, ask) : ''}
            </Button>
          </>
        }
      >
        {ask && u && (
          <div className="section-stack">
            {ask.action === 'apply' && <FieldTestReminder tests={v?.fieldTests} what={u.title} />}
            <ConfirmBody u={u} ask={ask} rows={rows} />
          </div>
        )}
      </Dialog>
      {leaving && (
        <LeaveOutDialog
          row={leaving}
          title={u?.title ?? ''}
          onClose={() => setLeaving(null)}
          onSave={async (note) => {
            await leaveOut(id, leaving, true, note, data.reload, setError);
            setLeaving(null);
          }}
        />
      )}
      {open && u && <DetailDrawer update={u} row={open} onClose={() => (setOpen(null), data.reload())} />}
      {toast && <Toast tone="ok" message={toast} onDismiss={() => setToast('')} />}
    </>
  );
}

const TITLES: Record<UpdateId, string> = {
  'app-calling': 'App calling',
  'voicemail-emails': 'Voicemail emails',
  'mailbox-policy': 'Old voicemail: make room',
  'patch-rings': 'Patch rings',
  'live-sessions': 'Live sessions',
  'number-inventory': 'Number inventory'
};
const titleOf = (id: UpdateId) => TITLES[id] ?? id;
/** Updates with a list per customer (mailboxes, people) behind Details. */
const hasDetail = (id: UpdateId) => id === 'voicemail-emails' || id === 'mailbox-policy' || id === 'patch-rings';

async function leaveOut(id: string, row: UpdateRow, on: boolean, note: string, reload: () => void, setError: (s: string) => void) {
  try {
    await up.leaveOut(id, row.customerId, on, note);
    reload();
  } catch (err) {
    setError(err instanceof Error ? err.message : String(err));
  }
}

function confirmTitle(u: UpdateInfo, a: Ask): string {
  const who = plural(a.ids.length, 'customer', 'customers');
  return a.action === 'check' ? `Check ${who}?` : a.action === 'apply' ? `${u.applyLabel}: ${who}?` : `${u.putBackLabel ?? 'Put back'}: ${who}?`;
}

function confirmButton(u: UpdateInfo, a: Ask): string {
  return a.action === 'check' ? 'Check' : a.action === 'apply' ? u.applyLabel : (u.putBackLabel ?? 'Put back');
}

function ConfirmBody({ u, ask, rows }: { u: UpdateInfo; ask: Ask; rows: UpdateRow[] }) {
  const names = rows.filter((r) => ask.ids.includes(r.customerId)).map((r) => r.name);
  const list: ReactNode = (
    <p className="muted">
      {names.slice(0, 8).join(', ')}
      {names.length > 8 ? ` and ${names.length - 8} more` : ''}.
    </p>
  );
  if (ask.action === 'check') return <>{list}<p>Checking only reads: nothing changes.</p></>;
  return (
    <>
      {list}
      <p>{ask.action === 'apply' ? u.about : `${u.putBackLabel ?? 'Put back'} undoes what this update did for them, as far as Stitch saved it.`}</p>
      <p className="muted">They run one customer after another in the background. Each is checked again when it’s done, and each is in Stitch’s log.</p>
    </>
  );
}

function LeaveOutDialog({ row, title, onClose, onSave }: { row: UpdateRow; title: string; onClose: () => void; onSave: (note: string) => Promise<void> }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Leave ${row.name} out of ${title}?`}
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await onSave(note);
              setBusy(false);
            }}
          >
            Leave out
          </Button>
        </>
      }
    >
      <p>It’s never counted as needing this update, and “all that need it” skips it. Put it back in at any time.</p>
      <div className="field">
        <label htmlFor="leave-out-note">Why (optional)</label>
        <input id="leave-out-note" className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder="For example: they keep their own setting" />
      </div>
    </Dialog>
  );
}

/** The policy the mailbox update sets (Stitch's standard: Make room for new messages). */
function StandardPolicy({ standard, choices, onSaved }: { standard: string; choices: { value: string; label: string; description?: string }[]; onSaved: (msg: string) => void }) {
  const [value, setValue] = useState(standard);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => setValue(standard), [standard]);
  const chosen = choices.find((c) => c.value === value);
  return (
    <Card title="What it sets">
      <div className="card-body section-stack">
        <div className="field">
          <label htmlFor="standard-policy">Old voicemail messages</label>
          <select id="standard-policy" className="input" value={value} onChange={(e) => setValue(e.target.value)}>
            {choices.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          {chosen?.description && <small className="muted">{chosen.description}</small>}
        </div>
        <small className="muted">New mailboxes get this when they’re made. Changing it doesn’t change any mailbox until you apply the update again.</small>
        {error && <Notice tone="crit">{error}</Notice>}
        <div className="row-actions">
          <Button
            size="small"
            variant="primary"
            disabled={busy || value === standard}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await up.setStandardPolicy(value);
                onSaved(`Saved: ${chosen?.label ?? value}.`);
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </Card>
  );
}

const MAILBOX_STATE: Record<'standard' | 'different' | 'unset' | 'company' | 'other' | 'unknown', { label: string; tone: Tone }> = {
  other: { label: 'Left alone', tone: 'neutral' },
  standard: { label: 'Has it', tone: 'ok' },
  different: { label: 'Different', tone: 'warn' },
  unset: { label: 'Company default', tone: 'warn' },
  company: { label: 'Changed by the company', tone: 'neutral' },
  unknown: { label: 'Couldn’t read', tone: 'crit' }
};

/** One customer's detail for an update: what the check looked at. */
export function DetailDrawer({ update, row, onClose }: { update: UpdateInfo; row: Pick<UpdateRow, 'customerId' | 'name' | 'domain'>; onClose: () => void }) {
  const d = useData(() => up.detail(update.id, row.customerId), [update.id, row.customerId], { live: false });
  const [busy, setBusy] = useState('');
  if (update.id === 'voicemail-emails' && d.data?.kind === 'voicemail-emails') {
    const company = d.data.company;
    return (
      <VoicemailMailboxes
        company={company}
        canChange={company.writable}
        busy={busy}
        go={async (action, scope) => {
          setBusy(`${action}`);
          try {
            return (await (action === 'move' ? vm.move(scope) : vm.putBack(scope))).run;
          } finally {
            setBusy('');
          }
        }}
        onClose={onClose}
      />
    );
  }
  return (
    <Drawer open onClose={onClose} title={`${update.title} at ${row.name}`} subtitle={row.domain ?? undefined}>
      <div className="section-stack">
        {d.error ? (
          <LoadError error={d.error} retry={d.reload} />
        ) : !d.data ? (
          <Loading label="Reading from the phone system" />
        ) : (
          <DetailBody detail={d.data} />
        )}
      </div>
    </Drawer>
  );
}

function DetailBody({ detail }: { detail: UpdateDetail }) {
  if (detail.kind === 'mailbox-policy') {
    if (!detail.rows.length) return <Empty title="No mailboxes">This company has no extensions on the phone system.</Empty>;
    return (
      <ul className="vm-mailbox-list" aria-label="Mailboxes">
        {detail.rows.map((m) => (
          <li key={m.extension}>
            <span className="cell-stack grow">
              <span>
                <strong style={{ fontWeight: 650 }}>{m.extension}</strong> {m.name}
                {m.box && <span className="muted"> · Voicemail box</span>}
              </span>
              <small>{[m.label, m.saved ? `Before Stitch: ${m.saved.label} (saved ${shortWhen(m.saved.at)})` : '', m.detail ?? ''].filter(Boolean).join(' · ')}</small>
            </span>
            <Chip small tone={MAILBOX_STATE[m.state].tone}>
              {MAILBOX_STATE[m.state].label}
            </Chip>
          </li>
        ))}
      </ul>
    );
  }
  if (detail.kind === 'patch-rings') {
    return (
      <>
        {!detail.available.ok && <Notice tone="warn">{detail.available.reason ?? 'Patch rings aren’t possible here.'}</Notice>}
        <dl className="dl">
          <dt>People who can have one</dt>
          <dd>{detail.people}</dd>
          <dt>Patch rings Stitch made</dt>
          <dd>{detail.ringsThere === null ? detail.rings : `${detail.ringsThere} of ${detail.rings} still there`}</dd>
          <dt>People without one</dt>
          <dd>{detail.missing ?? 'Couldn’t check'}</dd>
          <dt>Presence room</dt>
          <dd>{detail.presenceRoom ? (detail.presenceRoom.there === false ? 'Missing' : 'There') : 'Not made yet'}</dd>
        </dl>
        {detail.clashes.length > 0 && (
          <Notice tone="warn" title="Numbers already taken">
            {detail.clashes.map((c) => `${c.number}${c.extension ? ` (for ${c.extension})` : ''}: ${c.what}`).join('; ')}
          </Notice>
        )}
      </>
    );
  }
  return <p className="muted">Nothing more to show.</p>;
}

/**
 * A customer's updates, on its page in Stitch Admin → Customers: where it stands with each, and
 * Check, Apply and Put back for it alone. A customer being set up gets "Set up everything it needs".
 */
export function UpdatesCard({ customerId, name, linked, settingUp }: { customerId: string; name: string; linked: boolean; settingUp: boolean }) {
  const data = useData(() => up.forCustomer(customerId), [customerId]);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<UpdateInfo | null>(null);
  const [busy, setBusy] = useState('');
  const list = data.data?.updates ?? [];
  const going = list.some((u) => u.row.running);
  usePoll(going, data.reload);
  const needs = list.filter((u) => !u.row.leftOut && (u.row.state === 'needed' || u.row.state === 'partial' || u.row.state === 'unknown') && !u.row.blocked);

  const act = async (u: UpdateInfo | null, action: UpdateAction | 'setup') => {
    setBusy(`${u?.id ?? ''}:${action}`);
    setError('');
    try {
      if (action === 'setup') await up.setUp(customerId);
      else {
        const r = await up.run(u!.id, action, [customerId]);
        if (!r.started && r.skipped[0]) setError(r.skipped[0].why);
      }
      data.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  };

  return (
    <Card
      title="Updates"
      id="updates"
      action={
        linked && list.length ? (
          <span className="row-actions">
            {settingUp || needs.length ? (
              <Button size="small" variant="primary" onClick={() => void act(null, 'setup')} disabled={Boolean(busy) || going}>
                {settingUp ? 'Set up everything it needs' : 'Check and apply what it needs'}
              </Button>
            ) : null}
            <Link className="btn small ghost" to="/admin/updates">
              All updates
            </Link>
          </span>
        ) : undefined
      }
    >
      <div className="card-body section-stack">
        {!linked && <p className="muted">Link {name}’s phone system tenant first. Then each update shows here, with what it needs.</p>}
        {error && <Notice tone="crit">{error}</Notice>}
        {data.error ? (
          <LoadError error={data.error} retry={data.reload} />
        ) : !data.data ? (
          <Loading label="Loading updates" />
        ) : linked ? (
          <ul className="update-list">
            {list.map((u) => (
              <li key={u.id}>
                <span className="cell-stack grow">
                  <span className="update-title">
                    <strong>{u.title}</strong> <StateChip row={u.row} />
                  </span>
                  <small>{u.row.leftOut ? u.row.leftOutNote || 'Left out on purpose.' : u.row.blocked && u.row.state !== 'done' ? `${u.row.summary} ${u.row.blocked}` : u.row.summary}</small>
                  {u.row.lastAction && (
                    <small className="muted">{`${u.row.lastAction === 'apply' ? u.applyLabel : (u.putBackLabel ?? 'Put back')} by ${u.row.changedBy ?? 'Stitch'}${u.row.changedAt ? `, ${shortWhen(u.row.changedAt)}` : ''}: ${u.row.lastResult}`}</small>
                  )}
                </span>
                <span className="row-actions">
                  <Button size="small" variant="ghost" onClick={() => void act(u, 'check')} disabled={Boolean(busy) || Boolean(u.row.running)} aria-label={`Check ${u.title}`}>
                    Check
                  </Button>
                  {hasDetail(u.id) && (
                    <Button size="small" variant="ghost" onClick={() => setOpen(u)} aria-label={`Details: ${u.title}`}>
                      Details
                    </Button>
                  )}
                  {u.putBackLabel && (u.row.state === 'done' || u.row.state === 'partial' || u.row.lastAction === 'apply') && (
                    <Button size="small" onClick={() => void act(u, 'put_back')} disabled={Boolean(busy) || Boolean(u.row.running) || Boolean(u.row.blocked)} aria-label={`${u.putBackLabel}: ${u.title}`}>
                      {u.putBackLabel}
                    </Button>
                  )}
                  {u.row.state !== 'done' && u.row.state !== 'unavailable' && !u.row.leftOut && (
                    <Button size="small" variant="primary" onClick={() => void act(u, 'apply')} disabled={Boolean(busy) || Boolean(u.row.running) || Boolean(u.row.blocked)} aria-label={`${u.applyLabel}: ${u.title}`}>
                      {u.applyLabel}
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {open && <DetailDrawer update={open} row={{ customerId, name, domain: null }} onClose={() => (setOpen(null), data.reload())} />}
    </Card>
  );
}
